import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useToast } from "../../../contexts/ToastContext";
import { useUploadImageMutation } from "../../../store/api/imageUploadApi";
import type {
	Payment,
	UploadPaymentProofData,
} from "../../../store/api/paymentsApi";
import { useUploadPaymentProofMutation } from "../../../store/api/paymentsApi";

interface PaymentProofUploadModalProps {
	payment: Payment | null;
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

const PaymentProofUploadModal: React.FC<PaymentProofUploadModalProps> = ({
	payment,
	isOpen,
	onClose,
	onSuccess,
}) => {
	const [uploadImage, { isLoading: isUploadingImage }] =
		useUploadImageMutation();
	const [uploadProof, { isLoading: isUploadingProof }] =
		useUploadPaymentProofMutation();
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [referenceNumber, setReferenceNumber] = useState("");
	const [errors, setErrors] = useState<Record<string, string>>({});
	const { success, error: showError } = useToast();

	const isLoading = isUploadingImage || isUploadingProof;

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			setErrors({ file: "Please select a file" });
			return;
		}

		// Validate file type - allow jpg, jpeg, png, pdf
		const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
		const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf"];

		const isValidType = allowedTypes.includes(file.type?.toLowerCase());
		const isValidExtension = allowedExtensions.some(ext =>
			file.name?.toLowerCase().endsWith(ext)
		);

		if (!isValidType && !isValidExtension) {
			setErrors({ file: "Please select an image file (JPG, JPEG, PNG) or PDF" });
			return;
		}

		// Validate file size (5MB max)
		const maxSize = 5 * 1024 * 1024; // 5MB in bytes
		if (file.size > maxSize) {
			setErrors({ file: "File size must be less than 5MB" });
			return;
		}

		// Validate file size (minimum 1KB)
		if (file.size < 1024) {
			setErrors({
				file: "File size is too small. Please select a valid image.",
			});
			return;
		}

		// Clear previous errors
		setErrors((prev) => ({ ...prev, file: "" }));

		// Set file and create preview
		setImageFile(file);
		const reader = new FileReader();
		reader.onloadend = () => {
			if (reader.result && typeof reader.result === "string") {
				setImagePreview(reader.result);
			}
		};
		reader.onerror = () => {
			setErrors({ file: "Failed to read file. Please try again." });
			setImageFile(null);
			setImagePreview(null);
		};
		reader.readAsDataURL(file);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validation
		if (!payment) {
			const errorMsg =
				"Payment information is missing. Please refresh and try again.";
			setErrors({ submit: errorMsg });
			showError(errorMsg);
			return;
		}

		if (!payment.id) {
			const errorMsg = "Invalid payment ID. Please refresh and try again.";
			setErrors({ submit: errorMsg });
			showError(errorMsg);
			return;
		}

		if (!imageFile) {
			setErrors({ file: "Please select a payment proof image" });
			return;
		}

		// Clear previous errors
		setErrors({});

		try {
			// Convert file to base64
			const base64 = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onloadend = () => {
					if (!reader.result || typeof reader.result !== "string") {
						reject(new Error("Failed to read file"));
						return;
					}
					// Remove data:image/...;base64, prefix
					const parts = reader.result.split(",");
					if (parts.length < 2) {
						reject(new Error("Invalid file format"));
						return;
					}
					resolve(parts[1]);
				};
				reader.onerror = () => reject(new Error("Failed to read file"));
				reader.readAsDataURL(imageFile);
			});

			// Upload image to Cloudinary
			const uploadResult = await uploadImage({
				images: [base64],
				folder: "payment_proofs",
			}).unwrap();

			if (!uploadResult.uploaded || uploadResult.uploaded.length === 0) {
				const errorMsg =
					uploadResult.failed?.[0]?.error ||
					"Failed to upload image. Please try again.";
				throw new Error(errorMsg);
			}

			const uploadedImage = uploadResult.uploaded[0];
			if (!uploadedImage?.url) {
				throw new Error("Image upload succeeded but no URL was returned.");
			}

			// Upload payment proof with Cloudinary URL
			const uploadData: UploadPaymentProofData = {
				payment_proof_url: uploadedImage.url,
			};
			if (referenceNumber && referenceNumber.trim()) {
				uploadData.reference_number = referenceNumber.trim();
			}

			await uploadProof({
				id: payment.id,
				data: uploadData,
			}).unwrap();

			// Reset form
			setImageFile(null);
			setImagePreview(null);
			setReferenceNumber("");
			setErrors({});
			success(
				"Payment proof uploaded successfully. Awaiting admin verification.",
			);
			onSuccess();
		} catch (error: unknown) {
			console.error("Upload error:", error);
			let errorMessage = "Failed to upload payment proof. Please try again.";

			if (error && typeof error === "object") {
				if ("data" in error) {
					const errorData = error.data as { error?: string; message?: string };
					errorMessage = errorData.error || errorData.message || errorMessage;
				} else if ("message" in error && typeof error.message === "string") {
					errorMessage = error.message;
				}
			}

			setErrors({ submit: errorMessage });
			showError(errorMessage);
		}
	};

	const handleClose = () => {
		setImageFile(null);
		setImagePreview(null);
		setReferenceNumber("");
		setErrors({});
		onClose();
	};

	const formatCurrency = (amount: number | undefined): string => {
		if (amount === undefined || amount === null || isNaN(amount)) {
			return "₦0.00";
		}
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
		}).format(amount);
	};

	const formatDate = (dateString: string | undefined): string => {
		if (!dateString) return "N/A";
		try {
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return "Invalid date";
			return date.toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
			});
		} catch {
			return "Invalid date";
		}
	};

	if (!isOpen || !payment) return null;

	return (
		<>
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black/50 z-1300 animate-fade-in"
				onClick={handleClose}
			/>

			{/* Modal */}
			<div className="fixed inset-0 z-1400 flex items-center justify-center p-4">
				<div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
					{/* Header */}
					<div className="flex items-center justify-between p-6 border-b border-border">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Upload Payment Proof
							</h2>
							<div className="text-sm text-muted-foreground mt-1 space-y-1">
								{payment.property_name && (
									<p>
										Property:{" "}
										<span className="font-medium text-foreground">
											{payment.property_name}
										</span>
									</p>
								)}
								<p>
									Payment:{" "}
									<span className="font-medium text-foreground">
										{formatCurrency(payment.amount)}
									</span>{" "}
									• Due:{" "}
									<span className="font-medium text-foreground">
										{formatDate(payment.due_date)}
									</span>
								</p>
							</div>
						</div>
						<Button variant="ghost" size="icon" onClick={handleClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>

					{/* Content */}
					<form
						onSubmit={handleSubmit}
						className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]"
					>
						<div className="space-y-6">
							{errors.submit && (
								<div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
									{errors.submit}
								</div>
							)}

							{/* Image Upload */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Payment Proof Image{" "}
									<span className="text-destructive">*</span>
								</label>
								<div className="space-y-4">
									<div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
										<input
											type="file"
											accept="image/jpeg,image/jpg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
											onChange={handleFileChange}
											className="hidden"
											id="payment-proof-upload"
											disabled={isLoading}
										/>
										<label
											htmlFor="payment-proof-upload"
											className="cursor-pointer flex flex-col items-center space-y-2"
										>
											<Icon
												name="Upload"
												size={32}
												className="text-muted-foreground"
											/>
											<div>
												<p className="text-sm font-medium text-foreground">
													Click to upload or drag and drop
												</p>
												<p className="text-xs text-muted-foreground mt-1">
													PNG, JPG, GIF up to 5MB
												</p>
											</div>
										</label>
									</div>

									{imagePreview && (
										<div className="relative">
											<img
												src={imagePreview}
												alt="Payment proof preview"
												className="w-full max-h-64 object-contain rounded-lg border border-border"
											/>
											<Button
												type="button"
												variant="destructive"
												size="icon"
												className="absolute top-2 right-2"
												onClick={() => {
													setImageFile(null);
													setImagePreview(null);
												}}
												disabled={isLoading}
											>
												<Icon name="X" size={16} />
											</Button>
										</div>
									)}

									{errors.file && (
										<p className="text-sm text-destructive">{errors.file}</p>
									)}
								</div>
							</div>

							{/* Reference Number */}
							<div>
								<Input
									label="Reference Number (Optional)"
									type="text"
									placeholder="Transaction ID, Check #, etc."
									value={referenceNumber}
									onChange={(e) => setReferenceNumber(e.target.value)}
									disabled={isLoading}
								/>
							</div>

							{/* Instructions */}
							<div className="bg-muted/30 rounded-lg p-4">
								<h4 className="text-sm font-medium text-foreground mb-2">
									Upload Instructions:
								</h4>
								<ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
									<li>Take a clear photo or scan of your payment receipt</li>
									<li>
										Ensure all details are visible (amount, date, reference)
									</li>
									<li>File must be in image format (PNG, JPG, GIF)</li>
									<li>Maximum file size: 5MB</li>
								</ul>
							</div>
						</div>

						{/* Footer */}
						<div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-border">
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading || !imageFile}>
								{isLoading ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										Uploading...
									</>
								) : (
									<>
										<Icon name="Upload" size={16} className="mr-2" />
										Upload Proof
									</>
								)}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default PaymentProofUploadModal;
